import { useEffect, useRef } from 'react';
import { AppShell } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router';
import { useStore } from '@/store';
import { SearchModal } from '@/components/SearchModal';
import { ItemDetailModal } from '@/components/ItemDetailModal';
import { FloatingCompareButton } from '@/components/FloatingCompareButton';
import { CompareModal } from '@/components/CompareModal';
import { ProfileModal } from '@/components/ProfileModal';
import Header from './Header';
import BottomNav from './BottomNav';
import dayjs from 'dayjs';
import { urlQuery } from '@/lib';
import { login } from '@/client/sdk.gen';

type Props = {
  children: React.ReactNode;
};

function MainLayout({ children }: Props) {
  const { searchModalOpen, selectedItem, isIngameBrowser, updateState } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isIngameBrowser) {
      document.body.classList.add('is-in-game');
    } else {
      document.body.classList.remove('is-in-game');
    }
  }, [isIngameBrowser]);

  const lastScrollTop = useRef(0);

  useEffect(() => {
    const SCROLL_THRESHOLD = 50;

    const handleScroll = () => {
      const currentScrollTop = window.scrollY ?? document.documentElement.scrollTop;
      const delta = currentScrollTop - lastScrollTop.current;

      if (delta > SCROLL_THRESHOLD) {
        document.body.classList.remove('body-scrollup');
        document.body.classList.add('body-scrolldown');
        lastScrollTop.current = currentScrollTop;
      } else if (delta < -SCROLL_THRESHOLD) {
        document.body.classList.remove('body-scrolldown');
        document.body.classList.add('body-scrollup');
        lastScrollTop.current = currentScrollTop;
      }

      // Keep lastScrollTop in sync at edges
      if (currentScrollTop <= 0 || currentScrollTop >= document.documentElement.scrollHeight - window.innerHeight) {
        lastScrollTop.current = currentScrollTop <= 0 ? 0 : currentScrollTop;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('body-scrolldown', 'body-scrollup');
    };
  }, []);

  useEffect(() => {
    const garena_token = urlQuery('garena_token') || urlQuery('access_token') || sessionStorage.getItem('garena_token')
    let checkString = garena_token + dayjs().format('DD-MM-YYYY-mm') + import.meta.env.VITE_APP_PROJECT_NAME

    // if(garena_token && checkString != clientLogged && garena_token.length >= 64) {
    if (garena_token) {
      login({
        body: {
          token: garena_token
        }
      }).then(() => {
        updateState('loginSuccess', true)
        localStorage.setItem('garena_token', checkString)
        sessionStorage.setItem('garena_token', garena_token)
      })
    }
  }, [])

  const handleSearchClose = () => {
    updateState('searchModalOpen', false);
  };

  const handleItemDetailClose = () => {
    updateState('selectedItem', null);
    // Navigate back to the base route when closing item detail
    if (location.pathname.startsWith('/item/')) {
      navigate('/', { replace: true });
    } else if (location.pathname.startsWith('/outfits/')) {
      navigate('/outfits', { replace: true });
    } else if (location.pathname.startsWith('/guns/')) {
      navigate('/guns', { replace: true });
    } else if (location.pathname.startsWith('/collections/')) {
      navigate('/collections', { replace: true });
    }
  };

  const handleItemSelect = (item: any) => {
    updateState('selectedItem', item);
    // Update URL based on current route context
    if (location.pathname.startsWith('/outfits')) {
      navigate(`/outfits/${item.id}`, { replace: false });
    } else if (location.pathname.startsWith('/guns')) {
      navigate(`/guns/${item.id}`, { replace: false });
    } else if (location.pathname.startsWith('/collections')) {
      navigate(`/collections/${item.id}`, { replace: false });
    } else {
      navigate(`/item/${item.id}`, { replace: false });
    }
  };

  return (
    <>
      <AppShell
        header={{ height: 64 }}
        padding={0}
      >
        <AppShell.Header>
          <Header />
        </AppShell.Header>

        <AppShell.Main
          className='app-shell-main'
          style={{
            // paddingBottom: '88px', // Space for bottom nav (64px + 24px margin)
            minHeight: '100vh',
          }}
        >
          {children}
        </AppShell.Main>
      </AppShell>

      <BottomNav />

      <SearchModal
        opened={searchModalOpen || false}
        onClose={handleSearchClose}
        onItemSelect={handleItemSelect}
      />

      <ItemDetailModal
        item={selectedItem || null}
        opened={!!selectedItem}
        onClose={handleItemDetailClose}
        onItemSelect={handleItemSelect}
      />

      <FloatingCompareButton />
      <CompareModal />
      <ProfileModal />
    </>
  );
}

export default MainLayout;